import { Component, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { PatientLogin, UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { firstValueFrom } from "rxjs";
import { DuplicatePatientCheckResult } from "./duplicate-patient";
import { PlayerService } from "./player.service";
import { AccountService } from "@account/services";
import { ConfirmationService, MessageService } from "primeng/api";
import { Message } from "@bbwt/classes";
import { AccountStatus } from "@main/users";
import { CoreRole } from "@main/roles";
import { UserHelperService } from "@main/users/user-helper.service";
import { WalkInService } from "@project/walk-ins/classes/walk-in.service";
import { tap } from "rxjs";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { PatientSettings, SettingsSectionsName, SystemConfigurationService } from "@main/system-configuration";
import { Location } from "@angular/common";
import { removePathParam, replaceLastPathSegment } from "@bbwt/utils/routes";
import { FormEventType } from "@main/formio/components/form-events/event-type";
import { FormEventsService } from "@main/formio/services/formEvents.service";
import { FormEventPayload } from "@main/formio/dto/formEventsDTO";

@Component({
    selector: "edit-player",
    templateUrl: "./edit-player.component.html",
    styles: []
})
export class EditPlayerComponent implements OnInit {
    patientForm = this.fb.group({
        patient: [null],
        otherFields: this.fb.group({
            organizationGroups: [[], Validators.required]
        }),
        consentToUpdate: [false]
    });
    savingPatient = false;
    loadingPatient = true;
    runningUserAction = false;
    userSsoProvider: number;
    enableSendPasswordResetMailBtn: boolean;
    enableResendInvitationBtn: boolean;
    buttonsConfig = <FormButtonsConfig>{
        cancelFunc: () => this.tryGoLocationBack(true),
        saveLabel: "Save"
    };
    patientId: string;
    selfRegWalkInId: string;
    duplicatePatients: DuplicatePatientCheckResult;
    currentPatient: PatientLogin;
    duplicatesDialogVisible = false;
    returnUrl: string = null;
    episodeId: string = null;
    restorePrescription: boolean = false;

    isNeedsUpdating = false;
    isReviewed = false;
    isImportedAndNotReviwed = false;
    monthsUntilReview: number;
    from: string = null;
    createDateTime: string = null;
    walkInDateString: string = null;
    warningText: string = null;
    walkInDate: string = null;
    walkInId: string = null;
    consentToUpdateChanges = false;
    regNumberIsVisible: boolean;
    isAuthenticatorEnabled: boolean = false;
    allowedToReset2Fa: boolean = false;

    _orgsWithData: { id: string; name: string }[] = []; // Holds the list of organizations where the Patient has medical data (Episodes)
    _selectedOrgs: string[] = [];

    _errorMessages = { orgCantBeRemoved: "Organisation cannot be removed because the Patient has medical data associated with it." };

    formEventType = FormEventType.ReviewPatient;
    hasFormEvent = false;

    queryParams: { [key: string]: string |number| boolean  } = {};

    enabledPsdConfirmation: boolean = false;

    constructor(
        private playerService: PlayerService,
        private activatedRoute: ActivatedRoute,
        private walkInService: WalkInService,
        private userHelperService: UserHelperService,
        private confirmationService: ConfirmationService,
        private fb: UntypedFormBuilder,
        private router: Router,
        private location: Location,
        private patientService: PatientService,
        private userService: UserService,
        private accountService: AccountService,
        private messageService: MessageService,
        private namingConvService: NamingConventionService,
        private systemConfigurationService: SystemConfigurationService,
        private formEventsService: FormEventsService
    ) {}

    get organizationGroups() {
        return this.patientForm.get("otherFields")?.get("organizationGroups");
    }

    onEnabledPSDConfirmationChanged(value) {
        this.enabledPsdConfirmation = value;
    }

    get consentToUpdate() {
        return this.patientForm.get("consentToUpdate").value || !this.enabledPsdConfirmation;
    }

    cancelReviewImportedPatient() {
        if (!this.returnUrl) {
            return this.router.navigate(["/app/patients"]);
        }

        if (this.from == "appointments") {
            return this.router.navigate(["/app/appointments"], { queryParams: { initialDate: this.createDateTime } });
        }

        if (this.walkInId || this.walkInDateString || this.walkInDate) {
            return this.router.navigate(["/app/walk-ins"], {
                queryParams: { walkInDate: this.walkInDate ?? this.walkInDateString }
            });
        }

        return this.router.navigate(["/app/consultation-history"]);
    }

    tryGoLocationBack(cancel: boolean) {
        if (this.isImportedAndNotReviwed && cancel) {
            return this.cancelReviewImportedPatient();
        }

        if (this.returnUrl) {
            return this.router.navigate([this.returnUrl], { queryParams: this.queryParams });
        }

        if (cancel) {
            return this.router.navigate(["/app/patients"]);
        }
    }

    async ngOnInit() {
        const patientId: string = (await firstValueFrom(this.activatedRoute.params)).id;
        this.allowedToReset2Fa = this.userHelperService.isTopAdmin;
        await firstValueFrom(
            this.activatedRoute.queryParams.pipe(
                tap(params => {
                    this.returnUrl = params.returnUrl;
                    this.episodeId = params.episodeId;
                    this.from = params.from;
                    this.createDateTime = params.createDateTime;
                    this.walkInDateString = params.walkInDateString;
                    this.walkInDate = params.walkInDate;
                    this.walkInId = params.walkInId;
                    this.regNumberIsVisible = params.regNumberIsVisible == "false";
                })
            )
        );

        let loadPatient: Promise<PatientLogin>;
        if (patientId === "0") {
            var queryParams = await firstValueFrom(this.activatedRoute.queryParams);
            if (queryParams.offlineId) {
                loadPatient = this.playerService.get(queryParams.offlineId);
            } else {
                loadPatient = this.playerService.getFromPatient(queryParams.profileId);
            }
        } else {
            loadPatient = this.playerService.get(patientId);
        }

        const patient = await loadPatient;
        this.currentPatient = patient;

        this.loadingPatient = false;
        this.patientId = patientId;

        this._orgsWithData = await this.playerService.getOrganizationsWithMedicalData(this.currentPatient.patientId);

        this.patchPatientForm(patient);
        if (!patient.selfRegister) {
            this.patientService.currentPatientId = patient.patientId;
        }

        await this.checkNeedsUpdating();
        this.checkIsAuthenticatorEnabled();
        if (this.isNeedsUpdating || this.isImportedAndNotReviwed){
            this.hasFormEvent=!!(await this.formEventsService.getCurrentFormEvent(this.formEventType));
        }

        this.createQueryParams();
    }

    private patchPatientForm(patient?: PatientLogin) {
        if (!!patient) {
            if (patient.selfRegister) {
                patient.email ??= patient.selfRegEmail;
            }

            this.patientForm.patchValue({
                patient,
                otherFields: { organizationGroups: patient.organizationGroups },
                consentToUpdate: patient.consentToUpdate
            });
        }
        this.userSsoProvider = this.currentPatient.ssoProvider;
        this.refreshButtons(patient);
    }
    autosaveComplete = false;

    autoSavePatient = async () => {
        if (this.consentToUpdate) {
            await this.savePatient(true);
        }
        this.autosaveComplete = true;
    };

    async manualSavePatient() {
        if (!this.isReviewed) {
            const { patient } = await this.savePatient();
            if (this.patientId === "0" && patient.id != null) {
                this.patchPatientForm(patient);
                this.patientId = patient.id;
            }
        } else {
            await this.markAsReviewed();
            if (this.hasFormEvent) {
                this.formEventsService.triggerFormEvent(<FormEventPayload> {
                    formEventType: this.formEventType,
                    episodeId: null,
                    patientId:  this.currentPatient.patientId
                });
                return;
            }
        }
        this.warningText = null;
        this.tryGoLocationBack(false);
    }

    canApproveSelfRegistered() {
        return (
            this.currentPatient.selfRegister &&
            this.userHelperService.hasRole(
                CoreRole.ClinicalAdmin,
                CoreRole.Doctor,
                CoreRole.Therapist,
                CoreRole.StrengthAndConditioning,
                CoreRole.Nutritionist,
                CoreRole.SuperAdmin,
                CoreRole.MiProAdmin
            )
        );
    }

    async approveSelfRegisteredClicked() {
        if (this.patientForm.invalid) {
            this.patientForm.markAllAsTouched();
            return;
        }

        this.savingPatient = true;
        try {
            this.selfRegWalkInId = (await this.walkInService.getByPatientId(this.currentPatient.patientId)).id;
            this.duplicatePatients = await this.playerService.checkDuplicatesById(this.currentPatient.patientId);
            if (!!this.duplicatePatients) {
                this.duplicatesDialogVisible = true;
            } else {
                this.confirmationService.confirm({
                    message: `Are you sure you want to approve this Self Registered ${this.namingConvService.endUserSingular}?`,
                    accept: async () => {
                        await this.playerService.approveSelfRegisterPlayer(this.currentPatient.patientId);

                        this.patientService.triggerPatientsUpdated();
                        this.navigateToWalkInCreation();
                    }
                });
            }
        } finally {
            this.savingPatient = false;
        }
    }

    duplicatesCheckCompleted(event: any) {
        this.duplicatesDialogVisible = false;
        if (event && this.currentPatient.selfRegister) {
            this.navigateToWalkInCreation();
        }
    }

    private async savePatient(skipDupCheck?: boolean) {
        if (this.patientForm.invalid) {
            this.patientForm.markAllAsTouched();
            return;
        }
        this.selfRegWalkInId = null;
        this.savingPatient = true;
        try {
            let patient: PatientLogin = this.patientForm.get("patient").value;
            patient.selfRegister = this.currentPatient.selfRegister;
            patient.participationReason = this.currentPatient.participationReason;
            patient.consentToUpdate = this.patientForm.get("consentToUpdate").value;

            const otherFields = this.patientForm.get("otherFields").value;
            Object.assign(patient, otherFields);
            const patientOrDuplicates = await this.playerService.update(patient.id, patient, true);

            if (patientOrDuplicates) {
                if (
                    Array.isArray((<DuplicatePatientCheckResult>patientOrDuplicates).matches) ||
                    Array.isArray((<DuplicatePatientCheckResult>patientOrDuplicates).fuzzyNameMatches)
                ) {
                    this.duplicatePatients = <DuplicatePatientCheckResult>patientOrDuplicates;
                    this.currentPatient = patient;
                    this.duplicatesDialogVisible = true;
                } else {
                    const initialId = patient.id;

                    patient = <PatientLogin>patientOrDuplicates;
                    this.patientService.triggerPatientsUpdated(patient.patientId);

                    if (!this.userService.currentUser?.autoSaveForms) {
                        this.patchPatientForm(patient);
                    }

                    if (!initialId && patient.id) {
                        this.updateRouteWithUserIdAndRemoveProfileId(patient.id);
                    }

                    return { patient, otherFields: patient };
                }
            } else {
            }
        } catch (error) {
            if (error.error?.match(/^Email already in use, please update and try again$/)) {
                this.patientForm.get("email").setErrors({ emailInUse: true });
            } else if (error.error?.includes("Patient with the same Email already exists in this Organisation.")) {
                this.messageService.add(Message.Warning(error.error));
            }
        } finally {
            this.savingPatient = false;
            this.patientForm.enable();
        }
    }

    private async runUserAction(action: () => Promise<any>) {
        this.runningUserAction = true;
        try {
            await action();
        } finally {
            this.runningUserAction = false;
        }
    }

    async sendPasswordResetMail() {
        await this.runUserAction(() => this.accountService.recoverPassword({ email: this.currentPatient.userName }));
        this.messageService.add(Message.Success("The email has been sent.", "Password Reset"));
    }

    private async refreshButtons(patientLogin: PatientLogin) {
        this.enableSendPasswordResetMailBtn = patientLogin.accountStatus == AccountStatus.Active && !!patientLogin.email;
        this.enableResendInvitationBtn = patientLogin.accountStatus == AccountStatus.Invited;
    }

    async checkIsAuthenticatorEnabled() {
        if(this.currentPatient.email) {
           this.isAuthenticatorEnabled = await this.accountService.isAuthenticatorEnabled(this.currentPatient.email);
        }
    }

    reset2FaConfirmation() {
        this.confirmationService.confirm({
            message: "Are you sure want to reset user's 2FA?",
            accept: async () => {
                await this.runUserAction(() => this.accountService.disable2faForUser(this.currentPatient.id));
                this.messageService.add(Message.Success("The 2FA has been reset.", "Reset 2FA"));
            }
        });
    }

    async resendInvitation() {
        this.confirmationService.confirm({
            message: "Are you sure want to send invitation to selected user again?",
            accept: async () => {
                await this.runUserAction(() => this.playerService.resendInvitation(this.currentPatient.id));
                this.messageService.add(Message.Success("The invitation has been sent.", "Invitation"));
            }
        });
    }

    private navigateToWalkInCreation() {
        this.router.navigate(["/app/walk-ins"], {
            queryParams: {
                showPopulatedDialog: true,
                populatedDialogPatientId: this.currentPatient.patientId,
                populatedDialogClinicianId: this.userHelperService.userId,
                populatedDialogAdditionalNotes: this.currentPatient.participationReason
            }
        });
    }

    reloadPatient(patient: PatientLogin) {
        this.patchPatientForm(patient);
        if (patient?.patientId) {
            this.patientService.triggerPatientsUpdated(patient.patientId);
        }
    }

    async markAsReviewed() {
        this.isReviewed = false;
        await this.patientService.markAsReviewed(this.currentPatient.patientId);
        this.buttonsConfig = { ...this.buttonsConfig, saveLabel: "Save" };
    }

    private async checkNeedsUpdating() {
        if (this.patientService.currentPatientId) {
            let res = await this.patientService.checkIfPatientNeedsUpdating(this.patientService.currentPatientId);
            this.isNeedsUpdating = res.isNeedsUpdating;
            this.isImportedAndNotReviwed = res.isImportedAndNotReviwed;
        }

        if (this.isNeedsUpdating || this.isImportedAndNotReviwed) {
            this.isReviewed = true;
            this.monthsUntilReview = this.systemConfigurationService.getSettingsSection<PatientSettings>(
                SettingsSectionsName.PatientSettings
            )?.monthsUntilReview;
            this.buttonsConfig = { ...this.buttonsConfig, saveLabel: "Mark as Reviewed" };

            this.warningText = this.isImportedAndNotReviwed
                ? `The ${this.namingConvService.endUserSingular} has been Imported, it is necessary to review the patient data below`
                : `It's been ${this.monthsUntilReview} Months since you last reviewed this ${this.namingConvService.endUserSingular}`;

            this.patientForm.get("consentToUpdate").valueChanges.subscribe(_ => {
                this.consentToUpdateChanges = true;
            });

            this.patientForm.valueChanges.subscribe(_ => {
                if (!this.consentToUpdateChanges) {
                    this.buttonsConfig = { ...this.buttonsConfig, saveLabel: "Save" };
                    this.isReviewed = false;
                } else {
                    this.consentToUpdateChanges = false;
                }
            });
        }
    }

    organizationsChanged(orgIds: string[]) {
        if (
            this._orgsWithData.some(o => {
                if (!orgIds.includes(o.id)) {
                    this._errorMessages = {
                        orgCantBeRemoved: `Organisation "${o.name}" cannot be removed because the Patient has medical data associated with it.`
                    };
                    return true;
                }
                return false;
            })
        ) {
            this.patientForm.get("otherFields").get("organizationGroups").setErrors({ orgCantBeRemoved: true });
        } else {
            this.patientForm.get("otherFields").get("organizationGroups").setErrors(null);
        }
    }

    private updateRouteWithUserIdAndRemoveProfileId(userId: string) {
        let path = replaceLastPathSegment(this.location.path(), userId, true);
        path = removePathParam(path, "profileId");
        this.location.replaceState(path);
    }


    private createQueryParams(): void {
        if (this.episodeId) {
            this.queryParams ["episodeId"] = this.episodeId ;
        }

        if (this.walkInDateString) {
            this.queryParams["walkInDateString"] = this.walkInDateString;
        }

        if (this.walkInDate) {
            this.queryParams["walkInDate"] = this.walkInDate;
        }

        if (this.walkInId) {
            this.queryParams["walkInId"] = this.walkInId;
        }

        if (this.from == "prescription") {
            this.queryParams["restorePrescription"] = true;
        }

        if (this.createDateTime) {
            this.queryParams["currentEpisodeCreateDateTime"] = this.createDateTime;
        }

        if (this.from == "document") {
            this.queryParams["restoreDocument"] = true;
        }

        if (this.from == "walkIn") {
            this.queryParams["restoreWalkIn"] = true;
        }

        if (this.from == "appointments") {
            this.queryParams["initialDate"] = this.createDateTime;
            this.queryParams["restoreAppointment"] = true;
        }

        if (this.from) {
            this.queryParams["from"] = this.from;
        }
    }
}
